import type * as Highcharts from 'highcharts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getLastZone = (zones: Highcharts.SeriesZonesOptionsObject[]) => {
  if (zones.length) {
    return zones[zones.length - 1];
  }

  return null;
};

export const NO_DATA_ZONE = {
  dashStyle: 'Dot',
} as const;

export const DEFAULT_ZONE = {
  dashStyle: 'Solid',
} as const;

/**
 * Simple function to add a new zone to an existing zones array
 * Zones are defined by specifying an xValue they should run up to, together with a specific style object
 * @param {Array}   zones                          Array containing all zones currently used in the chart
 * @param {Number}  xValue                         The xValue the zone run up until
 * @param {Object}  [zoneProperties=DEFAULT_ZONE]  Properties object containing mainly styles for the zone
 */

export function addZone(
  zones: Highcharts.SeriesZonesOptionsObject[],
  xValue: number | null,
  zoneProperties: Highcharts.SeriesZonesOptionsObject = DEFAULT_ZONE,
): void {
  const zone: Highcharts.SeriesZonesOptionsObject = { ...zoneProperties };

  // The provided xValue can be `null` to indicate that it's a zone without any limit
  if (xValue) {
    zone.value = xValue;
  }

  // Add zone to list of existing zones
  zones.push(zone);
}

/**
 * Adds a no-data zone conform to our way of marking a specific point as no-data
 *
 * This applies to all charts used within the platform/app
 * No-data zones are by default marked by dashed lines before and after the point for which there is no data available
 * This function only works if previous no-data zones were added with the de-facto dashed line [NO_DATA_ZONE]
 * For more customisation and area-charts, extend or customise NO_DATA_ZONE and add your own custom styling attributes
 *
 * @param {Array}  zones                          Array containing all zones currently used in the chart
 * @param {Object} previousPoint                  The point before the no-data point
 * @param {Object} nextPoint                      The point after the no-data point
 * @param {Number} indexInChart                   The index of the no-data point in the chart
 * @param {Object} [zoneProperties=NO_DATA_ZONE]  Properties object containing mainly styles for the zone
 */

export function addNoDataZone(
  zones: Highcharts.SeriesZonesOptionsObject[],
  previousPoint: Pick<Highcharts.Point, 'x'>,
  nextPoint: Pick<Highcharts.Point, 'x'>,
  indexInChart: number,
  zoneProperties: Highcharts.SeriesZonesOptionsObject = NO_DATA_ZONE,
): void {
  // If this is the first point, and there were no zones
  // Make a zone up to this point representing a solid line
  if (!zones.length && indexInChart > 0 && previousPoint) {
    addZone(zones, previousPoint.x);
  }

  const lastZone = getLastZone(zones);

  if (lastZone) {
    if (lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
      // If last point was also soldout, extend previous zone point with the current x value
      lastZone.value = nextPoint.x;
    } else {
      // Dot zone spans over two points, mark previous point as end of last zone,
      // and mark next point as end of dot-zone. This results in a dotted zone around the X-marked point
      lastZone.value = previousPoint.x;
      addZone(zones, nextPoint.x, zoneProperties);
    }
  }
}

/**
 * Checks if two lines intersect
 * @param  { Number } line1StartX   : x coordinate of line 1 start point
 * @param  { Number } line1StartY   : y coordinate of line 1 start point
 * @param  { Number } line1EndX     : x coordinate of line 1 end point
 * @param  { Number } line1EndY     : y coordinate of line 1 end point
 * @param  { Number } line2StartX   : x coordinate of line 2 start point
 * @param  { Number } line2StartY   : y coordinate of line 2 start point
 * @param  { Number } line2EndX     : x coordinate of line 2 end point
 * @param  { Number } line2EndY     : y coordinate of line 2 end point
 * @return { Boolean }              : true if lines intersect, false otherwise
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function intersection(
  line1StartX: number,
  line1StartY: number,
  line1EndX: number,
  line1EndY: number,
  line2StartX: number,
  line2StartY: number,
  line2EndX: number,
  line2EndY: number,
) {
  const denominator =
    (line2EndY - line2StartY) * (line1EndX - line1StartX) -
    (line2EndX - line2StartX) * (line1EndY - line1StartY);

  if (denominator === 0) {
    return false;
  }

  let a = line1StartY - line2StartY;
  let b = line1StartX - line2StartX;
  const numerator1 =
    (line2EndX - line2StartX) * a - (line2EndY - line2StartY) * b;
  const numerator2 =
    (line1EndX - line1StartX) * a - (line1EndY - line1StartY) * b;
  a = numerator1 / denominator;
  b = numerator2 / denominator;

  // if we cast these lines infinitely in both directions, they intersect here:
  const x = line1StartX + a * (line1EndX - line1StartX);
  const y = line1StartY + a * (line1EndY - line1StartY);

  return { x, y };
}
